import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from '@features/tags/components/cells/ActiveBadge';
import { VarietyImageCell } from './components/cells/VarietyImageCell';
import { VarietyRowActions } from './components/cells/VarietyRowActions';
import type { Variety } from './schema';

export interface VarietyRowActionHandlers {
  onEdit: (row: Variety) => void;
}

export function buildVarietiesColumns(
  actions: VarietyRowActionHandlers,
): ColDef<Variety>[] {
  return [
    {
      headerName: 'Image',
      width: 70,
      sortable: false,
      filter: false,
      cellRenderer: VarietyImageCell,
    },
    {
      colId: 'crop',
      field: 'cropName',
      headerName: 'Crop',
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      colId: 'code',
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 140,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: (p: ICellRendererParams<Variety>) =>
        p.data ? (
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
            {p.data.code}
          </code>
        ) : null,
    },
    {
      colId: 'name',
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Variety>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Variety>) =>
        p.data ? <VarietyRowActions row={p.data} {...actions} /> : null,
    },
  ];
}
